import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TableSort } from "@src/shared/constants/sort-order.constants";
import type { RootState } from "@src/redux/store";
import {
  appUpdateService,
  type AppUpdateListItem,
  type AppUpdateDetail,
  type GetAppUpdatesParams,
  type CreateAppUpdatePayload,
  type UpdateAppUpdatePayload,
} from "@src/@api-services/appUpdate";

interface AppUpdateState {
  data: AppUpdateListItem[];
  selectedUpdate: AppUpdateDetail | null;
  total: number;
  params: GetAppUpdatesParams;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getAppUpdates = createAsyncThunk(
  "appUpdates/getAppUpdates",
  async (params: GetAppUpdatesParams, { rejectWithValue }) => {
    try {
      const response = await appUpdateService.getAppUpdates(params);
      const rawData = response.data?.data?.data ?? [];
      const totalRecord = response.data?.data?.totalRecord ?? 0;
      return {
        params,
        data: rawData as AppUpdateListItem[],
        total: Number(totalRecord),
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch app updates"
      );
    }
  }
);

export const getAppUpdateDetails = createAsyncThunk(
  "appUpdates/getAppUpdateDetails",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await appUpdateService.getAppUpdateById(id);
      return response.data?.data as AppUpdateDetail;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch app update details"
      );
    }
  }
);

export const createAppUpdate = createAsyncThunk(
  "appUpdates/createAppUpdate",
  async (
    payload: CreateAppUpdatePayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await appUpdateService.createAppUpdate(payload);
      const state = getState() as RootState;
      if (state.appUpdates?.params) {
        await dispatch(getAppUpdates(state.appUpdates.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to create app update"
      );
    }
  }
);

export const updateAppUpdate = createAsyncThunk(
  "appUpdates/updateAppUpdate",
  async (
    {
      id,
      updateData,
    }: {
      id: string | number;
      updateData: UpdateAppUpdatePayload;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await appUpdateService.updateAppUpdate(id, updateData);
      const state = getState() as RootState;
      if (state.appUpdates?.params) {
        await dispatch(getAppUpdates(state.appUpdates.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to update app update"
      );
    }
  }
);

export const deleteAppUpdate = createAsyncThunk(
  "appUpdates/deleteAppUpdate",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await appUpdateService.deleteAppUpdate(id);
      const state = getState() as RootState;
      if (state.appUpdates?.params) {
        await dispatch(getAppUpdates(state.appUpdates.params));
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to delete app update"
      );
    }
  }
);

const initialState: AppUpdateState = {
  data: [],
  selectedUpdate: null,
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

export const appUpdateSlice = createSlice({
  name: "appUpdates",
  initialState,
  reducers: {
    clearSelectedAppUpdate: (state) => {
      state.selectedUpdate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAppUpdates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppUpdates.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getAppUpdates.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load app updates";
      })
      .addCase(getAppUpdateDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppUpdateDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUpdate = action.payload;
      })
      .addCase(getAppUpdateDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load app update details";
      })
      .addCase(createAppUpdate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createAppUpdate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createAppUpdate.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppUpdate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateAppUpdate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppUpdate.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppUpdate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteAppUpdate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppUpdate.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearSelectedAppUpdate } = appUpdateSlice.actions;
export default appUpdateSlice.reducer;
